import { Injectable } from '@angular/core';
import { PostHogService } from './posthog.service';

@Injectable({
  providedIn: 'root'
})
export class PostHogUserProfileService {

  constructor(
    private _posthogService: PostHogService
  ) {}

  /**
   * Initialize user profile based on current authentication status
   * This method should be called manually when needed
   */
  initializeUserProfile(): void {
    // For now, we'll just identify as anonymous to avoid circular dependencies
    this.identifyAnonymousUser();
  }

  /**
   * Identify an authenticated user with PostHog
   * Call this method when you have user data available
   */
  identifyAuthenticatedUser(userData: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    isOwner?: boolean;
  }): void {
    try {
      // Identify the user with their actual ID and properties
      this._posthogService.identify(userData.id, {
        email: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        is_owner: userData.isOwner || false,
        user_type: 'authenticated',
        authentication_method: 'email_password'
      });

      // Set additional person properties
      this._posthogService.setPersonProperties({
        last_login: new Date().toISOString(),
        user_role: userData.isOwner ? 'owner' : 'user'
      });

      // Track successful authentication
      this._posthogService.track('user_authenticated', {
        user_id: userData.id,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error identifying authenticated user with PostHog:', error);
    }
  }

  /**
   * Identify an anonymous user with PostHog
   */
  private identifyAnonymousUser(): void {
    try {
      // Generate a unique anonymous ID
      const anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Identify as anonymous user
      this._posthogService.identify(anonymousId, {
        user_type: 'anonymous',
        authentication_status: 'not_authenticated'
      });

      // Set anonymous user properties
      this._posthogService.setPersonProperties({
        first_visit: new Date().toISOString(),
        visit_count: 1
      });

      // Track anonymous user creation
      this._posthogService.track('anonymous_user_created', {
        anonymous_id: anonymousId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error identifying anonymous user with PostHog:', error);
    }
  }

  /**
   * Update user properties when user data changes
   */
  updateUserProperties(properties: Record<string, any>): void {
    this._posthogService.setPersonProperties(properties);
  }

  /**
   * Track user logout
   */
  trackUserLogout(userId?: string): void {
    this._posthogService.track('user_logged_out', {
      user_id: userId || 'unknown',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user login
   */
  trackUserLogin(userId?: string): void {
    this._posthogService.track('user_logged_in', {
      user_id: userId || 'unknown',
      timestamp: new Date().toISOString(),
      login_method: 'email_password'
    });
  }

  /**
   * Reset PostHog user data (useful for logout)
   */
  resetUserProfile(): void {
    this._posthogService.reset();
  }

  /**
   * Get current user's PostHog distinct ID
   */
  getCurrentUserId(): string | null {
    return this._posthogService.getDistinctId();
  }

  /**
   * Check if current user is identified in PostHog
   */
  isUserIdentified(): boolean {
    const currentId = this.getCurrentUserId();
    return currentId !== null && !currentId.startsWith('anon_');
  }
}
